import { useEffect, useMemo, useRef, useState } from "react";

import { SavingOverlay } from "./SavingOverlay";

export interface LocationOption {
  icao: string;
  displayName: string;
  /**
   * City the airport serves. Not shown in the option label but included
   * in the searchable haystack — so typing "santiago" surfaces every
   * Santiago-area field even though the labels read "Pudahuel (SCEL)",
   * "Tobalaba (SCTB)", "Vitacura (SCLC)" etc.
   */
  city: string | null;
}

/** "{displayName} ({icao-without-sigil})" — the label shown to the user. */
function labelFor(o: LocationOption): string {
  const shown = o.icao.replace(/^_/, "");
  return `${o.displayName} (${shown})`;
}

/**
 * Typeahead combobox for picking an airport from the Aeropuertos
 * directory. When the typed text doesn't match anything in the list and
 * the parent provides `onCreate`, the top of the dropdown surfaces a
 * "+ Agregar" affordance that pops a small inline form (ICAO + city) so
 * the user can create a provisional airport on the spot, instead of
 * having to leave the flow.
 */
export function LocationCombobox({
  value,
  onChange,
  options,
  placeholder = "Empieza a escribir…",
  required,
  onCreate,
  topAirports,
}: {
  value: string | null;
  onChange: (icao: string | null) => void;
  options: LocationOption[];
  placeholder?: string;
  required?: boolean;
  /** ICAOs to pin at the top of the dropdown — typically the
   * `topAirports` list off the currently-selected aircraft. Rendered
   * before the rest, separated by a thin divider. Order is preserved
   * (admin-curated). Unknown / inactive ICAOs are dropped. */
  topAirports?: string[] | null;
  /**
   * Called when the user opts to add a brand-new airport for the typed
   * text. Receives the curated form values; should return the final
   * icao (possibly sigil-padded) on success, or throw on failure.
   */
  onCreate?: (args: {
    icao: string;
    displayName: string;
    city: string | null;
  }) => Promise<string>;
}) {
  const byIcao = useMemo(
    () => new Map(options.map((o) => [o.icao, o])),
    [options]
  );
  const [query, setQuery] = useState(
    value && byIcao.has(value) ? labelFor(byIcao.get(value)!) : ""
  );
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [creating, setCreating] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value && byIcao.has(value)) {
      setQuery(labelFor(byIcao.get(value)!));
    } else if (!value) {
      setQuery("");
    }
  }, [value, byIcao]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Split the dropdown into "top picks" (per-aircraft curated list)
  // and "rest" (alphabetical fallback). When the user starts typing
  // we collapse back into a single filtered list so search ranks
  // everything together.
  const { top, rest, divided } = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length > 0) {
      const matches = options
        .filter((o) => {
          const haystack = [
            o.icao,
            o.icao.replace(/^_/, ""),
            o.displayName,
            o.city ?? "",
          ]
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        })
        .slice(0, 30);
      return { top: [] as LocationOption[], rest: matches, divided: false };
    }
    const topList: LocationOption[] = [];
    if (topAirports && topAirports.length > 0) {
      const seen = new Set<string>();
      for (const icao of topAirports) {
        if (!icao || seen.has(icao)) continue;
        const hit = options.find((o) => o.icao === icao);
        if (hit) {
          topList.push(hit);
          seen.add(icao);
        }
      }
    }
    const topIds = new Set(topList.map((o) => o.icao));
    const restList = options.filter((o) => !topIds.has(o.icao)).slice(0, 12);
    return {
      top: topList,
      rest: restList,
      divided: topList.length > 0 && restList.length > 0,
    };
  }, [query, options, topAirports]);
  const filtered: LocationOption[] = [...top, ...rest];

  const term = query.trim();
  // Offer the "+ Agregar" row whenever there's typed text, no option
  // already matches it verbatim (label or city), and the parent
  // supports creation. This keeps the affordance at the TOP of the
  // dropdown so the user doesn't have to scroll past matches.
  const exactMatch = term
    ? filtered.find(
        (o) =>
          labelFor(o).toLowerCase() === term.toLowerCase() ||
          (o.city ?? "").toLowerCase() === term.toLowerCase()
      )
    : null;
  const offerCreate = !!onCreate && term.length > 0 && !exactMatch;

  function pick(opt: LocationOption) {
    onChange(opt.icao);
    setQuery(labelFor(opt));
    setOpen(false);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && open && filtered[highlight]) {
      e.preventDefault();
      pick(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        required={required}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
          const current = value ? byIcao.get(value) : null;
          if (current && labelFor(current) !== e.target.value) {
            onChange(null);
          }
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
        className="block w-full rounded-md border border-slate-300 px-3 py-2 pr-8 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
      />
      {query ? (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setQuery("");
            onChange(null);
            setOpen(false);
          }}
          aria-label="Borrar"
          className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <svg
            viewBox="0 0 20 20"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>
      ) : null}
      {open ? (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {offerCreate ? (
            <li
              onMouseDown={(e) => {
                e.preventDefault();
                setCreating(term);
                setOpen(false);
              }}
              className="cursor-pointer border-b border-slate-100 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-800 hover:bg-sky-100"
            >
              + Agregar “{term}” como nuevo aeropuerto
            </li>
          ) : null}
          {filtered.length === 0 ? (
            offerCreate ? null : (
              <li className="px-3 py-2 text-xs text-slate-500">
                Sin coincidencias.
              </li>
            )
          ) : (
            filtered.map((o, i) => {
              const isFirstOfRest = divided && i === top.length;
              return (
                <li
                  key={o.icao}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(o);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={
                    "cursor-pointer px-3 py-1.5 text-sm " +
                    (isFirstOfRest ? "border-t border-slate-200 " : "") +
                    (highlight === i
                      ? "bg-sky-100 text-slate-900"
                      : "text-slate-800")
                  }
                >
                  {labelFor(o)}
                </li>
              );
            })
          )}
        </ul>
      ) : null}

      {creating != null && onCreate ? (
        <CreateProvisionalAirportModal
          initialName={creating}
          onCancel={() => setCreating(null)}
          onConfirm={async ({ icao, displayName, city }) => {
            const finalIcao = await onCreate({ icao, displayName, city });
            onChange(finalIcao);
            const shown = finalIcao.replace(/^_/, "");
            setQuery(`${displayName} (${shown})`);
            setCreating(null);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateProvisionalAirportModal({
  initialName,
  onCancel,
  onConfirm,
}: {
  initialName: string;
  onCancel: () => void;
  onConfirm: (args: {
    icao: string;
    displayName: string;
    city: string | null;
  }) => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(initialName);
  const [icao, setIcao] = useState("");
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (!displayName.trim()) return setError("Nombre requerido.");
    const raw = icao.toUpperCase().trim();
    if (!/^[A-Z0-9]{3,4}$/.test(raw)) {
      return setError("Código debe ser 3 o 4 caracteres alfanuméricos.");
    }
    setSaving(true);
    try {
      await onConfirm({
        icao: raw,
        displayName: displayName.trim(),
        city: city.trim() || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={() => (saving ? undefined : onCancel())}
    >
      <SavingOverlay open={saving} label="Agregando aeropuerto…" />
      <div
        className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-slate-900">
          Agregar aeropuerto provisional
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Se crea como provisional; un admin lo curará al revisar el vuelo.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Nombre
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Código (ICAO o 3 letras)
              </label>
              <input
                type="text"
                value={icao}
                onChange={(e) => setIcao(e.target.value.toUpperCase())}
                placeholder="ej. SCQY"
                maxLength={4}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Ciudad (opcional)
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={80}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </div>
          </div>
          {error ? (
            <p className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
              {error}
            </p>
          ) : null}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {saving ? "Agregando…" : "Agregar"}
          </button>
        </div>
      </div>
    </div>
  );
}
